/**
 * ui/thumbnail-renderer.js
 * Renderer WebGL 128x128 dùng chung cho thumbnail thẻ Customizer (Trang phục, Kiểu tóc, Nhân vật mẫu).
 * Lớp này chỉ lo phần "vẽ": kiểm tra WebGL, dựng cảnh, cấu hình avatar mẫu, render, mã hoá ảnh, dispose.
 * Việc xếp hàng, lập lịch idle và cache nằm ở thumbnail-service.js.
 *
 * Tính xác định (ảnh không phụ thuộc thứ tự render):
 *  - kind 'preset': mỗi thumbnail dùng một BlockyAvatar MỚI dựng từ đúng preset (hình khối theo bodyType,
 *    sừng, cánh, đuôi, râu, kiểu mắt đều đúng) và được dispose ngay trước thumbnail kế tiếp.
 *  - kind 'outfit' / 'hair': dùng một avatar nền ("baseline") dựng một lần. Mỗi lần cấu hình, mọi thuộc tính
 *    ảnh hưởng tới ảnh đều được ghi đè lại hoàn toàn (tóc + màu tóc, quần, áo + màu áo) và các slot phụ kiện
 *    được ép về "none", nên trạng thái chỉ phụ thuộc (kind, value), không phụ thuộc thumbnail trước đó.
 */

import * as THREE from 'three';
import { BlockyAvatar } from '../../avatars/blocky-avatar.js';
import { AVATAR_PRESETS } from '../../avatars/avatar-presets.js';

export const THUMB_SIZE = 128;
const WEBP_QUALITY = 0.85;
const ENCODE_FALLBACK_MS = 300;
/** Số bước của initStep(). */
export const INIT_STEPS = 6;
const TEXTURE_SLOTS = ['map', 'emissiveMap', 'alphaMap', 'normalMap', 'roughnessMap'];

/** Màu tóc / màu áo cố định cho avatar nền để thumbnail ổn định. */
const BASE_HAIR_STYLE = 'dan_truong';
const BASE_HAIR_COLOR = '#6b4423';
const BASE_OUTFIT = 'so_mi';
const BASE_SHIRT_COLOR = '#ffffff';
const BASE_PANTS = 'long';

/** Góc máy cố định cho avatar nền (outfit lấy thân áo, hair lấy đầu): [vị trí camera], [điểm nhìn]. */
const FRAMING = {
  outfit: { pos: [0.3, 0.85, 2.4], look: [0, 0.75, 0] },
  hair: { pos: [0.5, 1.6, 1.7], look: [0, 1.46, 0] }
};
/** Preset: tự khung theo kích thước thật của nhân vật (trẻ em, người già, sừng, cánh, mũ). */
const PRESET_VIEW_OFFSET_X = 0.15; // tỉ lệ lệch ngang camera so với khoảng cách, cho góc nhìn 3/4

/** Slot phụ kiện và hàm gỡ tương ứng; luôn ép về none trên avatar nền. */
const ACCESSORY_SETTERS = [
  ['hat', 'setHat'],
  ['eyewear', 'setEyewear'],
  ['mask', 'setMask'],
  ['beard', 'setBeard'],
  ['bodyAcc', 'setBodyAccessory'],
  ['heldItem', 'setHeldItem'],
  ['horns', 'setHorns'],
  ['wings', 'setWings'],
  ['tail', 'setTail']
];

/** Đo thời gian một đoạn đồng bộ bằng performance.measure(name); detail (nếu có) ghi thumbnail đang xử lý. */
export function measured(name, fn, detail) {
  const start = performance.now();
  try {
    return fn();
  } finally {
    try {
      performance.measure(name, { start, end: performance.now(), detail });
    } catch {
      // Môi trường không hỗ trợ measure với tuỳ chọn: bỏ qua, không ảnh hưởng chức năng
    }
  }
}

/** Đưa avatar nền về cấu hình chuẩn đầy đủ trước mỗi thumbnail. */
function normalizeBaseline(avatar) {
  ACCESSORY_SETTERS.forEach(([slot, setter]) => {
    if (avatar.attachmentSlots[slot]) avatar[setter]('none');
  });
  const f = avatar.config.features;
  ['hat', 'eyewear', 'mask', 'beard', 'heldItem', 'bodyAccessory', 'horns', 'wings', 'tail'].forEach((k) => {
    if (f[k] && f[k] !== 'none') f[k] = 'none';
  });
  f.hasGlasses = false;
  f.hasBackpack = false;
  f.hasClipboard = false;
  if (f.eyeType && f.eyeType !== 'normal') avatar.setEyeType('normal');
}

function setHairIfChanged(avatar, style, color) {
  if (avatar.config.features.hairStyle !== style || avatar.config.colors.hair !== color) {
    avatar.setHair(style, color);
  }
}

export class ThumbnailRenderer {
  constructor() {
    /** null: chưa kiểm tra; true/false: kết quả kiểm tra WebGL của trang. */
    this.supported = null;
    /** true khi khởi tạo renderer thất bại: không thử lại trong phiên này. */
    this.failed = false;
    this.alive = false;
    this.canvas = null;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.baseline = null;
    this.sample = null;
    this.lights = [];
    /** Worker mã hoá ảnh: undefined = chưa tạo, null = không dùng được. */
    this.encoder = undefined;
    this.encodeSeq = 0;
  }

  /**
   * Kiểm tra hỗ trợ WebGL bằng một canvas thử rồi giải phóng context ngay (WEBGL_lose_context),
   * để không phải chặn/ẩn lỗi của THREE.WebGLRenderer. Canvas thử cùng kích thước canvas thumbnail.
   */
  probe() {
    if (this.supported !== null) return this.supported;
    let ok = false;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = THUMB_SIZE;
      canvas.height = THUMB_SIZE;
      const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
      ok = !!gl;
      const lose = gl && gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
    } catch {
      ok = false;
    }
    this.supported = ok;
    return ok;
  }

  /**
   * Khởi tạo chia INIT_STEPS bước để mỗi bước nằm trong một idle callback riêng, không tạo longtask:
   *  0: canvas + WebGLRenderer + scene + đèn;  1: avatar nền;  2: upload texture + compile shader;
   *  3: chờ link shader song song (async);  4: vẽ thử một lần;  5: tạo worker mã hoá.
   * Trả về boolean (hoặc Promise<boolean> ở bước 3); false là không dựng được, đánh dấu failed, không thử lại.
   */
  initStep(index) {
    if (this.failed) return false;
    try {
      if (index === 0) {
        if (!this.probe()) return false;
        this.#createContext();
      } else if (index === 1) {
        this.#createAvatar();
      } else if (index === 2) {
        this.#uploadTextures();
        this.renderer.compile(this.scene, this.camera);
      } else if (index === 3) {
        // Chờ driver link xong shader song song rồi mới vẽ thử, để lần vẽ đầu không phải chờ link
        return this.renderer.compileAsync(this.scene, this.camera).then(
          () => true,
          () => {
            this.failed = true;
            this.dispose();
            return false;
          }
        );
      } else if (index === 4) {
        // Vẽ thử một lần để chi phí khởi tạo GPU không rơi vào thumbnail đầu tiên
        this.renderer.render(this.scene, this.camera);
      } else {
        this.#getEncoderWorker();
        this.alive = true;
      }
      return true;
    } catch {
      this.failed = true;
      this.dispose();
      return false;
    }
  }

  /** Khởi tạo toàn bộ các bước (dùng cho test và nơi không cần chia nhỏ). */
  async init() {
    if (this.alive) return true;
    for (let i = 0; i < INIT_STEPS; i++) if (!(await this.initStep(i))) return false;
    return true;
  }

  #createContext() {
    const canvas = document.createElement('canvas');
    canvas.width = THUMB_SIZE;
    canvas.height = THUMB_SIZE;
    canvas.addEventListener(
      'webglcontextlost',
      (e) => {
        e.preventDefault();
        if (this.canvas === canvas) this.alive = false;
      },
      false
    );
    this.canvas = canvas;

    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(THUMB_SIZE, THUMB_SIZE, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(30, 1, 0.1, 20);

    const key = new THREE.DirectionalLight(0xffffff, 1.8);
    key.position.set(2, 4, 3);
    const fill = new THREE.DirectionalLight(0xb0d4ff, 0.8);
    fill.position.set(-2, 1, 2);
    const ambient = new THREE.AmbientLight(0xffffff, 1.1);
    this.lights = [key, fill, ambient];
    this.lights.forEach((l) => this.scene.add(l));
  }

  #createAvatar() {
    this.baseline = new BlockyAvatar('TEACHER', { isArticulated: true });
    normalizeBaseline(this.baseline);
    this.scene.add(this.baseline.root);
    this.#frame('outfit');
  }

  #frame(kind) {
    const f = FRAMING[kind];
    if (f) {
      this.camera.position.set(...f.pos);
      this.camera.lookAt(...f.look);
    } else {
      this.#frameSample();
    }
    this.camera.updateMatrixWorld(true);
  }

  /** Khung nửa người trên (đầu tới hông) theo chiều cao thật; mở rộng khi có cánh/đuôi rộng hơn khung. */
  #frameSample() {
    const av = this.sample;
    av.root.updateMatrixWorld(true);
    const head = new THREE.Box3().setFromObject(av.parts.head);
    const torso = new THREE.Box3().setFromObject(av.parts.torso);
    const top = head.max.y;
    const width = Math.max(Math.abs(torso.min.x), Math.abs(torso.max.x)) * 2;
    const band = Math.max(0.85, top * 0.62, width * 1.05);
    const centerY = top + 0.06 - Math.max(0.85, top * 0.62) / 2;
    const dist = band / 2 / Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2));
    this.camera.position.set(dist * PRESET_VIEW_OFFSET_X, centerY + band * 0.03, dist);
    this.camera.lookAt(0, centerY, 0);
  }

  #clearSample() {
    if (!this.sample) return;
    this.scene.remove(this.sample.root);
    this.sample.dispose();
    this.sample = null;
  }

  /** Cấu hình cảnh cho một thumbnail (kind: 'outfit' | 'hair' | 'preset'). */
  configure(kind, value) {
    if (!this.alive) throw new Error('thumbnail renderer is not alive');
    this.#clearSample();

    if (kind === 'preset') {
      const key = AVATAR_PRESETS[value] ? value : 'TEACHER';
      this.sample = new BlockyAvatar(key, { isArticulated: true });
      this.baseline.root.visible = false;
      this.scene.add(this.sample.root);
      this.sample.update(0);
    } else {
      const av = this.baseline;
      av.root.visible = true;
      normalizeBaseline(av);
      if (kind === 'outfit') {
        setHairIfChanged(av, BASE_HAIR_STYLE, BASE_HAIR_COLOR);
        av.setPants(BASE_PANTS);
        av.setOutfit(value);
      } else {
        av.setPants(BASE_PANTS);
        av.setOutfit(BASE_OUTFIT, BASE_SHIRT_COLOR);
        setHairIfChanged(av, value, BASE_HAIR_COLOR);
      }
      av.update(0);
    }
    this.#frame(kind);
  }

  render() {
    if (!this.alive) throw new Error('thumbnail renderer is not alive');
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Biên dịch shader của avatar mẫu hiện tại bằng compileAsync (KHR_parallel_shader_compile):
   * driver biên dịch song song nên main thread không bị chặn. Trả về Promise, phần đồng bộ rất nhỏ.
   */
  compile() {
    if (!this.alive) throw new Error('thumbnail renderer is not alive');
    this.#uploadTextures();
    return this.renderer.compileAsync(this.scene, this.camera);
  }

  /** Upload trước các canvas texture đã đổi để render() không phải chịu chi phí upload. */
  #uploadTextures() {
    const seen = new Set();
    this.scene.traverseVisible((obj) => {
      const mats = obj.material ? (Array.isArray(obj.material) ? obj.material : [obj.material]) : [];
      mats.forEach((m) => {
        for (const key of TEXTURE_SLOTS) {
          const tex = m[key];
          if (tex && !seen.has(tex)) {
            seen.add(tex);
            this.renderer.initTexture(tex);
          }
        }
      });
    });
  }

  /**
   * Mã hoá khung hình hiện tại thành ảnh webp. Trả về Promise<Blob|string|null>:
   *  1. Ưu tiên createImageBitmap + worker (OffscreenCanvas.convertToBlob): chụp nhanh rồi đọc ngược/mã hoá
   *     ngoài main thread, tránh toBlob trên canvas WebGL chặn main thread chờ GPU.
   *  2. Không có API trên thì dùng canvas.toBlob.
   *  3. Quá 300ms chưa có kết quả, hoặc các nhánh trên lỗi: dùng toDataURL (kết quả là chuỗi dataURL).
   */
  encode() {
    const canvas = this.canvas;
    return new Promise((resolve) => {
      let settled = false;
      let timer = null;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        resolve(value);
      };
      const viaDataUrl = () =>
        measured('vas-thumb-encode', () => {
          try {
            return canvas.toDataURL('image/webp', WEBP_QUALITY);
          } catch {
            return null;
          }
        });

      if (!canvas) {
        finish(null);
        return;
      }
      timer = setTimeout(() => finish(viaDataUrl()), ENCODE_FALLBACK_MS);
      this.#encodeAsync(canvas).then(
        (blob) => (blob ? finish(blob) : finish(viaDataUrl())),
        () => finish(viaDataUrl())
      );
    });
  }

  async #encodeAsync(canvas) {
    const worker = this.#getEncoderWorker();
    if (worker && typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(canvas);
      return measured('vas-thumb-encode', () => this.#postToEncoder(worker, bitmap));
    }
    if (typeof canvas.toBlob === 'function') {
      return new Promise((resolve) => {
        measured('vas-thumb-encode', () => canvas.toBlob((blob) => resolve(blob || null), 'image/webp', WEBP_QUALITY));
      });
    }
    return null;
  }

  #getEncoderWorker() {
    if (this.encoder === null || typeof Worker !== 'function' || typeof OffscreenCanvas !== 'function') return null;
    if (!this.encoder) {
      try {
        this.encoder = new Worker(new URL('./thumbnail-encoder.worker.js', import.meta.url), { type: 'module' });
      } catch {
        this.encoder = null; // không tạo được worker: dùng nhánh toBlob
        return null;
      }
    }
    return this.encoder;
  }

  #postToEncoder(worker, bitmap) {
    return new Promise((resolve, reject) => {
      const id = ++this.encodeSeq;
      const cleanup = () => {
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
      };
      const onMessage = (e) => {
        if (e.data.id !== id) return;
        cleanup();
        if (e.data.error) reject(new Error(e.data.error));
        else resolve(e.data.blob);
      };
      const onError = (e) => {
        cleanup();
        this.encoder = null; // worker hỏng: các lần sau dùng nhánh toBlob
        worker.terminate();
        reject(e.error || new Error('encoder worker error'));
      };
      worker.addEventListener('message', onMessage);
      worker.addEventListener('error', onError);
      worker.postMessage({ id, bitmap, type: 'image/webp', quality: WEBP_QUALITY }, [bitmap]);
    });
  }

  /** Giải phóng avatar, scene, renderer và context WebGL. An toàn khi gọi nhiều lần. */
  dispose() {
    try {
      this.#clearSample();
      if (this.baseline) {
        if (this.scene) this.scene.remove(this.baseline.root);
        this.baseline.dispose();
      }
      if (this.scene) this.lights.forEach((l) => this.scene.remove(l));
      if (this.renderer) {
        this.renderer.dispose();
        this.renderer.forceContextLoss();
      }
    } catch {
      // Context có thể đã mất: không có gì thêm để giải phóng
    } finally {
      if (this.encoder) this.encoder.terminate();
      this.encoder = undefined;
      this.baseline = null;
      this.sample = null;
      this.lights = [];
      this.scene = null;
      this.camera = null;
      this.renderer = null;
      this.canvas = null;
      this.alive = false;
    }
  }
}
